import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Divider,
    IconButton,
    CircularProgress,
    Alert,
    Stack,
    Tooltip,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    Close as CloseIcon,
    CheckCircle as CompleteIcon,
    Description as FileIcon,
    FunctionsOutlined as FormulaIcon,
    Image as ImageIcon,
    OpenInNew as OpenInNewIcon,
    PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import WorkOrderService from "../../../services/WorkOrder.service";
import { WO_STATUS_META, TRAVELER_STATUS_META } from "./statusMeta";

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        return format(parseISO(dateStr), "MM/dd/yyyy");
    } catch {
        return dateStr;
    }
}

function formatDateTime(dateStr) {
    if (!dateStr) return "—";
    try {
        return format(parseISO(dateStr), "MM/dd/yyyy h:mm a");
    } catch {
        return dateStr;
    }
}

function toNumber(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function formatQty(value) {
    const n = toNumber(value, null);
    if (n === null) return value ?? "—";
    return parseFloat(n.toFixed(4)).toString();
}

function fileIcon(name = "") {
    const lower = name.toLowerCase();
    if (lower.endsWith(".pdf")) return <PdfIcon fontSize="small" color="error" />;
    if ([".png", ".jpg", ".jpeg", ".gif", ".webp"].some((e) => lower.endsWith(e)))
        return <ImageIcon fontSize="small" color="primary" />;
    return <FileIcon fontSize="small" color="action" />;
}

function getEffectiveRouting(routing, part) {
    if (routing) return routing;
    // FA/SA parts store routing lines under routings[], not directly on the part
    if (part?.routings?.length > 0) {
        return part.routings.find((r) => r.is_default) ?? part.routings[0];
    }
    return null;
}

function collectMaterialRows(routing, part, workOrderQty = 0, resolvedPreview = null) {
    const rows = [];
    const woQty = toNumber(workOrderQty, 0);
    const effectiveRouting = getEffectiveRouting(routing, part);

    // Resolved-preview entries are keyed by part_number — the only identifier
    // they share with the routing lines below (they come from a plain
    // qty-check dict, not a serialized line).
    const resolvedByPartNumber = {};
    (resolvedPreview?.materials ?? []).forEach((m) => {
        resolvedByPartNumber[m.part_number] = m;
    });

    const pushLine = (line, type) => {
        const resolved = resolvedByPartNumber[line.material_detail?.part_number];
        const usesFormula = !!line.quantity_formula;
        const qtyPerUnit =
            resolved && resolved.qty_per_unit != null && !resolved.error
                ? resolved.qty_per_unit
                : line.quantity;
        const totalQty =
            resolved && resolved.qty_needed != null && !resolved.error
                ? resolved.qty_needed
                : toNumber(line.quantity, 0) * woQty;
        rows.push({
            key: `${type.toLowerCase()}-${line.id}`,
            type,
            part: line.material_detail?.full_part_number || line.material_detail?.part_number || "—",
            description: line.material_detail?.description || "—",
            unit: line.material_detail?.uom_detail?.abbreviation || "",
            qtyPerUnit,
            totalQty,
            notes: line.notes || "",
            usesFormula,
            formulaUnit: line.quantity_formula_uom_detail?.abbreviation || "",
            formulaError: resolved?.error || null,
        });
    };

    const matLines = Array.isArray(effectiveRouting?.material_lines) ? effectiveRouting.material_lines : [];
    matLines.forEach((line) => pushLine(line, "Material"));

    const hwLines = Array.isArray(effectiveRouting?.hardware_lines) ? effectiveRouting.hardware_lines : [];
    hwLines.forEach((line) => pushLine(line, "Hardware"));

    return rows;
}

function collectOperationRows(routing, part, resolvedPreview = null) {
    const effectiveRouting = getEffectiveRouting(routing, part);
    const opLines = Array.isArray(effectiveRouting?.operation_lines) ? effectiveRouting.operation_lines : [];

    const resolvedByStep = {};
    (resolvedPreview?.operations ?? []).forEach((o) => {
        resolvedByStep[o.step_number] = o;
    });

    return opLines
        .slice()
        .sort((a, b) => (a.step_number || 0) - (b.step_number || 0))
        .map((line) => {
            const resolved = resolvedByStep[line.step_number];
            const usesFormula = !!line.hours_formula;
            const estimatedHours =
                resolved && resolved.estimated_hours != null && !resolved.error
                    ? resolved.estimated_hours
                    : (line.estimated_hours ?? "—");
            return {
                key: `operation-${line.id}`,
                step: line.step_number,
                name: line.name,
                workCenter: line.work_center_detail?.name || "—",
                rateType: line.rate_type || "—",
                estimatedHours,
                usesFormula,
                formulaError: resolved?.error || null,
            };
        });
}

export default function WorkOrderViewDialog({ open, onClose, workOrder, onChanged }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [travelers, setTravelers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [completingId, setCompletingId] = useState(null);
    const [error, setError] = useState("");
    const [resolvedPreview, setResolvedPreview] = useState(null);

    const refresh = useCallback(async () => {
        if (!workOrder) return;
        setLoading(true);
        setError("");
        try {
            const res = await WorkOrderService.listTravelers(workOrder.id);
            const results = Array.isArray(res.data) ? res.data : res.data.results || [];
            setTravelers(results);
        } catch {
            setError("Failed to load traveler steps.");
        } finally {
            setLoading(false);
        }
    }, [workOrder]);

    useEffect(() => {
        if (open) refresh();
    }, [open, refresh]);

    // Resolve any formula-driven material/operation lines against this WO's
    // saved attribute values, so the preview shows what will actually be
    // consumed/take rather than the routing's nominal planning numbers.
    useEffect(() => {
        if (!open || !workOrder?.id) { setResolvedPreview(null); return; }
        let cancelled = false;
        WorkOrderService.resolvedPreview(workOrder.id)
            .then((res) => { if (!cancelled) setResolvedPreview(res.data); })
            .catch(() => { if (!cancelled) setResolvedPreview(null); });
        return () => { cancelled = true; };
    }, [open, workOrder?.id]);

    const part = workOrder?.part_detail || null;
    const routing = workOrder?.routing_detail || null;
    const materialRows = useMemo(
        () => collectMaterialRows(routing, part, workOrder?.quantity, resolvedPreview),
        [routing, part, workOrder?.quantity, resolvedPreview],
    );
    const operationRows = useMemo(
        () => collectOperationRows(routing, part, resolvedPreview),
        [routing, part, resolvedPreview],
    );
    const documents = part?.documents_detail || [];
    const attachments = workOrder?.attachments_detail || [];
    const configValues = useMemo(
        () =>
            (workOrder?.config_values || [])
                .slice()
                .sort(
                    (a, b) =>
                        (a.attribute_detail?.sort_order ?? 0) - (b.attribute_detail?.sort_order ?? 0) ||
                        (a.attribute_detail?.key ?? "").localeCompare(b.attribute_detail?.key ?? ""),
                ),
        [workOrder?.config_values],
    );

    if (!workOrder) return null;

    const statusMeta = WO_STATUS_META[workOrder.status] || {
        label: workOrder.status,
        color: "default",
    };
    const woQty = toNumber(workOrder.quantity, 0);

    const handleComplete = async (traveler) => {
        setCompletingId(traveler.id);
        setError("");
        try {
            await WorkOrderService.completeTraveler(traveler.id);
            await refresh();
            onChanged?.();
        } catch (err) {
            const detail =
                err.response?.data?.detail ||
                err.response?.data?.non_field_errors?.[0] ||
                "Could not complete this step.";
            setError(detail);
        } finally {
            setCompletingId(null);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
            <DialogTitle>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }} noWrap>
                            {workOrder.wo_number}
                        </Typography>
                        <Chip label={statusMeta.label} size="small" color={statusMeta.color} />
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                            {workOrder.part_display || "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {workOrder.part_description || ""}
                        </Typography>
                    </Box>
                    {workOrder.project_detail && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {workOrder.project_detail.number} · {workOrder.project_detail.name}
                        </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 0.5, mt: 1, flexWrap: "wrap" }}>
                        <Chip label={`Qty ${workOrder.quantity ?? "—"}`} size="small" variant="outlined" />
                        <Chip
                            label={`Received ${workOrder.received_qty || 0}/${workOrder.quantity || 0}`}
                            size="small"
                            variant="outlined"
                            color={
                                workOrder.quantity && workOrder.received_qty >= workOrder.quantity
                                    ? "success"
                                    : "default"
                            }
                        />
                        <Chip
                            label={`${formatDate(workOrder.planned_start)} – ${formatDate(workOrder.planned_finish)}`}
                            size="small"
                            variant="outlined"
                        />
                        {routing && (
                            <Chip label={`Routing: ${routing.name}`} size="small" variant="outlined" />
                        )}
                    </Box>
                    {workOrder.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {workOrder.notes}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Created {formatDateTime(workOrder.created)}
                        {workOrder.created_by_name ? ` by ${workOrder.created_by_name}` : ""}
                    </Typography>
                </Box>

                {configValues.length > 0 && (
                    <>
                        <Divider sx={{ mb: 1.5 }} />
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                            Configuration
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                            {configValues.map((cv) => (
                                <Chip
                                    key={cv.id}
                                    size="small"
                                    variant="outlined"
                                    label={`${cv.attribute_detail?.label ?? cv.attribute_detail?.key ?? cv.attribute}: ${
                                        cv.value || "—"
                                    }${cv.attribute_detail?.unit ? ` ${cv.attribute_detail.unit}` : ""}`}
                                />
                            ))}
                        </Box>
                    </>
                )}

                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Raw Materials
                </Typography>
                {materialRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No raw materials found for this work order.
                    </Typography>
                ) : (
                    <Stack spacing={0.75} sx={{ mb: 2 }}>
                        {materialRows.map((row) => (
                            <Box
                                key={row.key}
                                sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1 }}
                            >
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="body2" fontWeight={700} noWrap sx={{ fontFamily: "monospace" }}>
                                            {row.part}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                            {row.description}
                                        </Typography>
                                    </Box>
                                    <Chip label={row.type} size="small" />
                                </Box>
                                <Box sx={{ display: "flex", gap: 0.5, mt: 0.75, flexWrap: "wrap" }}>
                                    <Chip label={`Per Unit ${formatQty(row.qtyPerUnit)}${row.unit ? ` ${row.unit}` : ""}`} size="small" variant="outlined" />
                                    <Chip label={`Total ${formatQty(row.totalQty)}${row.unit ? ` ${row.unit}` : ""}`} size="small" variant="outlined" />
                                    {row.usesFormula && (
                                        <Tooltip
                                            title={
                                                row.formulaError
                                                    ? `Formula error: ${row.formulaError}`
                                                    : row.formulaUnit
                                                    ? `Quantity resolved from this work order's attributes (formula computes in ${row.formulaUnit}, converted to ${row.unit})`
                                                    : "Quantity resolved from this work order's attributes"
                                            }
                                        >
                                            <Chip
                                                icon={<FormulaIcon sx={{ fontSize: "0.8rem !important" }} />}
                                                label={row.formulaError ? "error" : "formula"}
                                                size="small"
                                                color={row.formulaError ? "error" : "info"}
                                                variant="outlined"
                                            />
                                        </Tooltip>
                                    )}
                                </Box>
                                {row.notes && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                        {row.notes}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Stack>
                )}

                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Work Centers
                </Typography>
                {operationRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No work centers found for this work order.
                    </Typography>
                ) : (
                    <Stack spacing={0.75} sx={{ mb: 2 }}>
                        {operationRows.map((row) => {
                            const hoursPerUnit = toNumber(row.estimatedHours, 0);
                            const totalHours = hoursPerUnit * woQty;
                            return (
                                <Box
                                    key={row.key}
                                    sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1 }}
                                >
                                    <Typography variant="body2" fontWeight={700} noWrap>
                                        {row.step}. {row.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                        {row.workCenter} · {row.rateType}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 0.5, mt: 0.75, flexWrap: "wrap" }}>
                                        <Chip label={`Hrs/Unit ${formatQty(row.estimatedHours)}`} size="small" variant="outlined" />
                                        <Chip label={`Total Hrs ${formatQty(totalHours)}`} size="small" variant="outlined" />
                                        {row.usesFormula && (
                                            <Tooltip
                                                title={
                                                    row.formulaError
                                                        ? `Formula error: ${row.formulaError}`
                                                        : "Hours resolved from this work order's attributes"
                                                }
                                            >
                                                <Chip
                                                    icon={<FormulaIcon sx={{ fontSize: "0.8rem !important" }} />}
                                                    label={row.formulaError ? "error" : "formula"}
                                                    size="small"
                                                    color={row.formulaError ? "error" : "info"}
                                                    variant="outlined"
                                                />
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                )}

                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Assembly Documents
                </Typography>
                {documents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No assembly documents attached.
                    </Typography>
                ) : (
                    <Stack spacing={0.5} sx={{ mb: 2 }}>
                        {documents.map((doc) => (
                            <Box
                                key={doc.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    px: 1.5,
                                    py: 0.75,
                                }}
                            >
                                {fileIcon(doc.title)}
                                <Typography variant="body2" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {doc.title || `Document ${doc.id}`}
                                </Typography>
                                {doc.document_url && (
                                    <Tooltip title="Open">
                                        <IconButton size="small" component="a" href={doc.document_url} target="_blank" rel="noopener noreferrer">
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        ))}
                    </Stack>
                )}

                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Work Order Attachments
                </Typography>
                {attachments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No Work Order documents attached.
                    </Typography>
                ) : (
                    <Stack spacing={0.5} sx={{ mb: 2 }}>
                        {attachments.map((att) => (
                            <Box
                                key={att.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    px: 1.5,
                                    py: 0.75,
                                }}
                            >
                                {fileIcon(att.title)}
                                <Typography variant="body2" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {att.title || `Attachment ${att.id}`}
                                </Typography>
                                {att.document_url && (
                                    <Tooltip title="Open">
                                        <IconButton size="small" component="a" href={att.document_url} target="_blank" rel="noopener noreferrer">
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        ))}
                    </Stack>
                )}

                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Traveler Steps
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : travelers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        {workOrder.status === "draft"
                            ? "This work order has not been released yet. Release it to generate traveler steps."
                            : "No traveler steps found for this work order."}
                    </Typography>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {travelers.map((t) => {
                            const meta = TRAVELER_STATUS_META[t.status] || {
                                label: t.status,
                                color: "default",
                            };
                            const isApproved = t.status === "approved";
                            return (
                                <Box
                                    key={t.id}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        p: 1,
                                        border: 1,
                                        borderColor: "divider",
                                        borderRadius: 1,
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap>
                                            {t.step_number}. {t.step_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                            {t.work_center_name}
                                            {t.assignee_names?.length
                                                ? ` · ${t.assignee_names.join(", ")}`
                                                : ""}
                                        </Typography>
                                        <Chip
                                            label={meta.label}
                                            size="small"
                                            color={meta.color}
                                            sx={{ mt: 0.5, height: 20, fontSize: "0.7rem" }}
                                        />
                                        {isApproved && t.approved_at && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                noWrap
                                                sx={{ mt: 0.5 }}
                                            >
                                                Approved by {t.completed_by_name || "Unknown user"} on{" "}
                                                {new Date(t.approved_at).toLocaleString()}
                                            </Typography>
                                        )}
                                    </Box>
                                    {isApproved ? (
                                        <CompleteIcon color="success" />
                                    ) : (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleComplete(t)}
                                            disabled={completingId === t.id}
                                        >
                                            {completingId === t.id ? "…" : "Complete"}
                                        </Button>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
